import React, { FC } from 'react';

import './Footer.css';

interface MktBrandLogo {
  logo: string;
  url: string;
  title: string;
}

export interface MktFooterLinkProps {
  url: string;
  label: string;
}

interface MktLink {
  icon: string;
  url: string;
  title: string;
}

export interface MktFooterNavigationLinksProps {
  title: string;
  links: MktFooterLinkProps[];
}

export interface MktFooterSocialLinksProps {
  linkedinLink: string;
  twitterLink: string;
  youtubeLink: string;
  appleStoreLink: string;
}

export interface MktFooterProps {
  navigationLinks: MktFooterNavigationLinksProps[];
  socialLinks?: MktLink[];
  appStoreLink?: MktLink;
  showSocialAndAppStoreLinks?: boolean;
  showDisclaimer?: boolean;
  emailContact?: string;
  brandLogos?: MktBrandLogo[];
  footerLogo: string;
  copyrightInfo?: string;
}

export const MktFooter: FC<MktFooterProps> = ({
  navigationLinks,
  socialLinks,
  showDisclaimer = true,
  emailContact,
  brandLogos,
  footerLogo,
  copyrightInfo,
  showSocialAndAppStoreLinks = true,
  appStoreLink,
}) => {
  return (
    <footer className="wh-flex wh-flex-col wh-items-center wh-gap-6 wh-bg-blue-dark wh-px-3 wh-py-[79px] wh-text-white lg:wh-px-[48px] lg:wh-py-[71px] xl:wh-items-start 2xl:wh-mx-auto 2xl:wh-items-center">
      <div className="wh-flex wh-max-w-[1278px] wh-flex-col wh-flex-wrap lg:wh-w-full ">
        <div className="wh-flex wh-w-full wh-flex-col lg:wh-flex-row wh-gap-6 lg:wh-gap-5 lg:wh-justify-between ">
          <div className="wh-flex wh-flex-col lg:wh-flex-row wh-gap-6 lg:wh-gap-5">
            <div className="wh-flex-col wh-items-start wh-gap-y-4 xs:wh-flex">
              {footerLogo && (
                <img
                  className="wh-h-[48px] wh-w-[47.99px]"
                  src={footerLogo}
                  alt="Securitize"
                  height={48}
                />
              )}
              <div className="wh-flex wh-flex-col   xs:wh-w-[153px]">
                <div className="wh-flex wh-w-full wh-flex-col wh-items-start">
                  {copyrightInfo && (
                    <p className="wh-text-center wh-text-[9.99px] wh-leading-[17.99px]">
                      {copyrightInfo}
                    </p>
                  )}
                  <p className="wh-text-center wh-text-[9.99px] wh-leading-[17.99px]">
                    All rights reserved
                  </p>
                  {emailContact && (
                    <p className="wh-text-center wh-text-[9.99px] wh-leading-[17.99px]">
                      {emailContact}
                    </p>
                  )}
                </div>
                {brandLogos && brandLogos.length >= 1 && (
                  <div className="wh-flex wh-items-center wh-gap-3 wh-pt-[8px] wh-flex-wrap">
                    {brandLogos.map(logo => {
                      return (
                        <div key={logo.title}>
                          <a href={logo.url} target="_blank">
                            <img src={logo.logo} alt={logo.title} />
                          </a>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="wh-flex wh-items-start wh-justify-between ">
              <div className="wh-box-border wh-flex wh-flex-wrap wh-justify-between lg:wh-justify-start wh-gap-5 xs:wh-w-[327px] lg:wh-mx-[0] lg:wh-min-w-[517px] lg:wh-w-auto lg:wh-px-[0px]">
                {navigationLinks.map((link: MktFooterNavigationLinksProps) => {
                  return (
                    <div
                      key={link.title}
                      className="xs:wh-w-[45%] lg:wh-w-auto"
                    >
                      <nav className="xs:wh-w-full lg:wh-w-auto xs:wh-whitespace-nowrap lg:wh-whitespace-normal">
                        <h2 className="wh-text-base wh-font-medium wh-text-white">
                          {link.title}
                        </h2>
                        <ul className="wh-flex wh-flex-col wh-gap-2 wh-pt-2">
                          {link.links.map((link: MktFooterLinkProps) => {
                            return (
                              <li key={link.label} className="wh-text-xs">
                                <a href={link.url}> {link.label}</a>
                              </li>
                            );
                          })}
                        </ul>
                      </nav>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
          {showSocialAndAppStoreLinks && (
            <div className=" wh-flex wh-flex-col wh-w-[144px] lg:wh-items-end wh-gap-3 lg:wh-pr-0">
              {appStoreLink && (
                <div className="wh-flex wh-w-full wh-justify-start wh-gap-3 lg:wh-pr-0">
                  <a href={appStoreLink.url} target="_blank">
                    <img
                      src={appStoreLink.icon}
                      alt={appStoreLink.title}
                      className="wh-w-[140px] wh-h-auto"
                    />
                  </a>
                </div>
              )}
              {socialLinks && socialLinks.length >= 1 && (
                <div className="wh-flex wh-w-full wh-justify-start wh-gap-3 lg:wh-pr-0 wh-flex-wrap">
                  {socialLinks.map(link => {
                    return (
                      <a
                        target="_blank"
                        rel="noreferrer"
                        className="wh-flex wh-h-[38.46px] wh-w-[36.67px] wh-items-center wh-justify-center wh-rounded-md wh-bg-white"
                        href={link.url}
                        key={link.title}
                      >
                        <img src={link.icon} alt={link.title} />
                      </a>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        <div className="wh-flex wh-flex-col wh-gap-6 xs:wh-mx-auto xs:wh-w-[327px]  lg:wh-mx-[86px]  lg:wh-w-[660px] lg:wh-flex-row lg:wh-items-center"></div>
      </div>
      <div className="wh-h-[2px] wh-w-full wh-max-w-[327px] wh-bg-gold lg:wh-max-w-[1278px] " />
      {showDisclaimer && (
        <div className="wh-flex wh-max-w-[327px] wh-flex-col wh-gap-3 lg:wh-max-w-[1278px]">
          <p className="wh-text-justify wh-text-[12px] wh-leading-[19.99px] wh-text-white-off">
            Check the background of Securitize Markets on{' '}
            <a
              className="wh-underline"
              href="https://brokercheck.finra.org/"
              target="_blank"
            >
              FINRA BrokerCheck
            </a>
            .
          </p>
          <p className="wh-text-justify wh-text-[12px] wh-leading-[19.99px] wh-text-white-off">
            Securities are offered through Securitize Markets, LLC, (“Securitize
            Markets”) a registered broker-dealer and member{' '}
            <a
              className="wh-underline"
              href="https://www.finra.org/#/"
              target="_blank"
            >
              FINRA
            </a>
            /
            <a
              className="wh-underline"
              href="https://www.sipc.org/"
              target="_blank"
            >
              SIPC
            </a>
            . Securitize Markets may provide recommendations, at times, but does
            not provide investment advice nor ongoing advice or monitoring of
            your investments. No communication through herein or in any other
            medium should be construed as such. Securities offered on the
            Securitize Markets ATS have not been registered under the Securities
            Act of 1933 and may not be offered or sold in the United States
            absent registration or an applicable exemption from registration
            requirements. Assets listed herein, such as digital assets or tokens
            using blockchain, are speculative, involve a high degree of risk,
            are generally illiquid, may have no value, have limited regulatory
            certainty, are subject to potential market manipulation risks and
            may expose investors to loss of principal. Investments in private
            placements are also speculative and involve a high degree of risk.
            Investors must be able to afford the loss of their entire
            investment. Eligibility to buy and sell securities on the Securitize
            Markets ATS is determined by Securitize Markets in its sole
            discretion. Offers to sell, or the solicitations of offers to buy
            any security can only be made through official offering documents
            that contain important information about risks, fees and expenses
            associated with the applicable securities available for trading on
            the Securitize Markets ATS. Investors should conduct their own due
            diligence, not rely on the financial assumptions or estimates
            displayed herein, and are encouraged to consult with a financial
            advisor, attorney, accountant, tax advisors, and any other
            professional that can help you to understand and assess the risks
            associated with any investment opportunity. Past performance is not
            indicative of future results. Neither the Securities and Exchange
            Commission nor any federal or state securities commission or
            regulatory authority has recommended or approved any investment or
            the accuracy or completeness of any of the information or materials
            provided herein or through any references/links herein. Further,
            there can be no assurance that any valuations provided by issuers
            are accurate or in agreement with market or industry valuations.
            Securitize Markets and its affiliates make no representations or
            warranties as to the accuracy of such information. Securitize
            Markets may collect certain information about you that helps us
            comply with various securities regulations and rules and the USA
            PATRIOT Act, a Federal law that requires all securities firms to
            obtain, verify, and record information that identifies each
            applicant. The information also helps us more fully understand your
            investment profile and identify what types of investments or
            strategies may be suitable for you. The term “Investors” used on
            this website, typically refers to accredited investors where
            applicable. Please note: if we cannot verify the information you
            provide, we may be required to restrict or deny your account.
            Trading during Extended Hours Trading Sessions carries unique risks,
            such as greater price volatility, lower liquidity, wider bid/ask
            spreads, and less market visibility, and may not be appropriate for
            all investors. There is no guarantee that a diversified portfolio
            will enhance overall returns, outperform a non-diversified
            portfolio, or prevent against loss. By accessing this site and any
            pages thereof, you agree to be bound by our{' '}
            <a className="wh-underline" href="/terms-conditions">
              Terms of Service
            </a>{' '}
            and{' '}
            <a className="wh-underline" href="/privacy-policy">
              Privacy Policy
            </a>
            .
          </p>

          <p className="wh-text-justify wh-text-[12px] wh-leading-[19.99px] wh-text-white-off">
            Crypto services are provided by Zero Hash and are not associated
            with Securitize Markets, LLC or its affiliates. Crypto assets are
            not protected by the Securities Investor Protection Corporation
            (SIPC), under the Securities Investor Protection Act (SIPA), or by
            federal securities laws. SIPC protection does not apply to funds
            held at SIPC-member broker-dealers if they are unrelated to the
            purchase or sale of eligible securities, intended for buying certain
            crypto assets not recognized as &quot;securities&quot; under SIPA,
            or transferred to a crypto asset platform. Crypto assets are neither
            cash nor cash equivalents and are not covered by the Federal Deposit
            Insurance Corporation (FDIC). The value of crypto assets is subject
            to significant market volatility. There is an increased risk of
            fraud or cyber attack associated with crypto assets and other forms
            of virtual currency.
          </p>
        </div>
      )}
    </footer>
  );
};
